import { treatment_program } from "../constants/common";

/**
 * Decides whether a profile link should point at an organisation (treatment
 * program / group practice) or at the individual, and supplies the treatment
 * program id the profile endpoint needs.
 *
 * Mirrors the rule the backend already uses when it attributes a post to an
 * entity (PostController::getPostData's addresses join):
 *
 *   treatment_program_id IS NULL                              -> individual
 *   display_individual_profile = 1 AND by_treatment_program!=1 -> individual
 *   otherwise                                                  -> organisation
 *
 * Call sites previously each invented their own test -- roles_num === 1, the
 * post's by_treatment_program alone, or the raw account_type -- which is why an
 * org profile could route to the account creator's individual page.
 *
 * @param {object} user  post owner / card subject (PostOwnerResource shape)
 * @param {object} post  optional post the link is rendered from
 * @returns {{ uuid: string, tid: string, accountType: string, isOrg: boolean }}
 */
export const getProfileTarget = (user, post = null) => {
  const uuid = user?.uuid ?? "";

  // The id is nested; reading treatment_program_info.id directly yields
  // undefined, which is why tid was never sent.
  const tid =
    user?.treatment_program_info?.treatment_program?.id ??
    user?.treatment_program_info?.id ??
    user?.treatmentProgramInfo?.treatmentProgram?.id ??
    "";

  const byTreatmentProgram =
    post?.by_treatment_program ?? user?.by_treatment_program ?? 0;

  const displaysIndividually = Number(user?.display_individual_profile) === 1;

  // No link to an organisation at all -> always the individual.
  let isOrg = Boolean(tid);

  // Linked to an org, but the person is shown individually and this post was
  // not published as the org -> the individual is the right target.
  if (isOrg && displaysIndividually && Number(byTreatmentProgram) !== 1) {
    isOrg = false;
  }

  // Fallback for payloads that carry no treatment_program_info (e.g. suggested
  // people): trust the account type rather than silently downgrading to user.
  if (!tid && user?.account_type === treatment_program) {
    isOrg = true;
  }

  return {
    uuid,
    tid: isOrg ? String(tid ?? "") : "",
    accountType: isOrg ? treatment_program : "user",
    isOrg,
  };
};

export default getProfileTarget;
